<template>
  <div class="flex flex-col w-full h-full relative">
    <floor-plan-with-navigation
      :mode="floorplanMode"
      @table-selected="tableSelected"
      @merge-tabs="doMergeTabs"
      @assign-table="doAssignTable"
      @swap-tables="doSwapTables"
    />

    <mode-selector v-model="floorplanMode" class="z-10" />
    <new-tab v-if="showNewTab && selectedTable" :table="selectedTable" @close="closeModals" />
    <tab-selector
      v-if="tabIdsToSelect"
      :tab-ids-to-select="tabIdsToSelect"
      @tab-selected="tabSelected"
      @close="tabIdsToSelect = null"
    />
    <merge-tabs v-if="mergingTabId" :tab-id="mergingTabId" @close="mergingTabId = null" />
    <swap-tables v-if="swapTableId" :table-id="swapTableId" @close="swapTableId = null" />
    <assign-table v-model:is-active="showAssignTablePopup" :tab-ids="assignTableTabIds" />
  </div>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useDialog } from '@last/core-ui/paprika'

import FloorPlanWithNavigation from '@/modules/floorplan/FloorPlanWithNavigation.vue'
import AssignTable from '@/modules/tabs/AssignTable.vue'
import MergeTabs from '@/modules/tabs/MergeTabs.vue'
import NewTab from '@/modules/tabs/NewTab.vue'
import SwapTables from '@/modules/tabs/SwapTables.vue'
import { useFloorplanStore } from '@/store/floorplan'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { Reservation, TableWithTabs } from '@/types'

import ModeSelector from './ModeSelector.vue'
import TabSelector from './TabSelector.vue'

const router = useRouter()
const floorplanStore = useFloorplanStore()
const reservationsStore = useReservationsStore()
const tabsStore = useTabsStore()

const { floorplanMode } = storeToRefs(floorplanStore)
const { searchReservation } = storeToRefs(reservationsStore)

const { editReservation } = reservationsStore
const { openTabWithCustomer } = tabsStore

const dialog = useDialog()
const { t } = useI18n()

const mergingTabId = ref<string | null>(null)
const assignTableTabIds = ref<string[]>([])
const showAssignTablePopup = ref<boolean>(false)
const swapTableId = ref<string | null>(null)

const selectedTable = ref<TableWithTabs | null>(null)

const showNewTab = ref(false)

const tabIdsToSelect = ref<string[] | null>(null)

function doMergeTabs(tabId: string) {
  mergingTabId.value = tabId
}

function doAssignTable(tabIds: string[]) {
  assignTableTabIds.value = tabIds
  showAssignTablePopup.value = true
}

function doSwapTables(tableId: string) {
  swapTableId.value = tableId
}

function tableSelected(table: TableWithTabs) {
  if (table.tabIds.length === 1) {
    router.push({
      name: 'orderManagement',
      params: { tabId: table.tabIds[0] }
    })
  } else if (table.tabIds.length > 1) {
    tabIdsToSelect.value = table.tabIds
  } else {
    selectedTable.value = table
    const reservation = searchReservation.value(table.id)
    if (reservation) {
      dialog({
        title: t('reservations.modal-title'),
        content: t('reservations.modal-text-info', {
          name: reservation.name,
          seats: reservation.diners,
          time: format(new Date(reservation.dateTime), 'HH:mm')
        }),
        mainLabel: t('reservations.cta-open-reservation'),
        secondaryLabel: t('reservations.cta-use-meanwhile'),
        onConfirm: () => openReservation(reservation),
        onSecondary: () => {
          showNewTab.value = true
        }
      })
    } else {
      showNewTab.value = true
    }
  }
}

function closeModals() {
  selectedTable.value = null
  showNewTab.value = false
}

function openReservation(reservation: Reservation) {
  const tabId = openTabWithCustomer({
    tableIds: reservation.tables,
    tab: {
      seats: reservation.diners
    },
    customer: {
      name: reservation.name,
      surname: reservation.surname ?? '',
      phoneNumber: reservation.phoneNumber,
      email: reservation.email
    },
    reservationId: reservation.id
  })
  editReservation({ ...reservation, tabId, status: 'ongoing' })
  router.push({ name: 'orderManagement', params: { tabId } })
}

function tabSelected(tabId: string) {
  tabIdsToSelect.value = null
  router.push({
    name: 'orderManagement',
    params: { tabId }
  })
}
</script>
